import { parseResponse } from '$lib/client/responseHelpers';
import Emitter from '$lib/services/streaming/eventsDispatcher';
import Events from '$lib/services/streaming/eventsDispatcher/events';
import type { CallPlayerConfig, PlayerConfigCommon, RTCPeerConnectionExtended } from '$lib/services/streaming/types';

import { callCloseTracks, callDeliveryConfig, callDeliveryRenegotiate } from '@api/streaming/streaming';
import type { CloseTracks, RemoteTrackObject, TracksToPullObject } from '@api/types';
import { streamingLoggerStore } from '@components/room/streaming-debugger/stores/logger';
import { LogType } from '@shared/types';
import { PlayerStatus, StreamingType } from '@shared/types/room';

import { createPeerConnection, ensureArray } from './common';

export class Player {
    private peerConnection?: RTCPeerConnectionExtended;
    private deliverySessionId?: string;

    private statsInterval?: NodeJS.Timeout;
    private mediaTimeoutOccured = false;
    private timeoutThresholdCounter = 0;
    private bytesReceived = 0;
    private mediaTimeoutThreshold = 30_000;
    private msStatsInterval = 2_000;

    private trackTimeout: Array<NodeJS.Timeout> = [];
    private pulledTracks?: RemoteTrackObject[];

    public async startDelivery(config: CallPlayerConfig & PlayerConfigCommon) {
        streamingLoggerStore.log({
            type: LogType.AppBroadcast,
            message: 'CloudflareCall: Delivery: Start',
        });
        if (this.pulledTracks) {
            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: 'CloudflareCall: Delivery: tracks is not closed before pull',
            });
            this.clear();
        }
        if (!this.peerConnection || this.peerConnection.connectionState === 'closed') {
            this.peerConnection = createPeerConnection() as RTCPeerConnectionExtended;
            this.peerConnection.streamingType = StreamingType.Delivery;
            this.peerConnection.connectionId = window.crypto?.getRandomValues(new Uint32Array(1))?.[0] || 1;
            this.attachPeerConnectionEvents(config.onEvent);
            this.startCollectingPlayerStats();
        }
        const peer = this.peerConnection;
        const tracks = ensureArray(config.tracks);
        const videoElement = config.video;
        const remoteSessionId = config.sessionId;
        if (!tracks || !remoteSessionId || !tracks.length) {
            console.error('No tracks to pull or sessionId');
            config.onEvent && config.onEvent(PlayerStatus.Error);
            return;
        }
        const tracksToPull = tracks.map((trackName) => ({
            location: 'remote',
            trackName: trackName,
            sessionId: remoteSessionId,
        }));

        streamingLoggerStore.log({
            type: LogType.AppDelivery,
            message: 'CloudflareCall: Delivery: pulling tracks',
        });
        const pullResponse = await this.pullTracks(tracksToPull, this.deliverySessionId);

        if (!pullResponse) {
            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: 'CloudflareCall: Delivery: tracks is not pulled',
            });
            console.error('Tracks is not pulled');
            this.clear();
            config.onEvent && config.onEvent(PlayerStatus.Error);
            return null;
        }

        const pullTrackError = pullResponse.tracks.find((track) => !!track.errorCode);
        if (pullTrackError) {
            this.statsCallback('rtc_empty_tracks', { errorText: pullTrackError.errorDescription });
            streamingLoggerStore.log(
                {
                    type: LogType.AppDelivery,
                    message: 'CloudflareCall: Delivery: tracks error: ' + pullTrackError.errorDescription,
                },
                { errorCode: pullTrackError.errorCode, errorDescription: pullTrackError.errorDescription },
                { includeExtendedData: ['errorCode', 'errorDescription'] }
            );
            console.error(`Error to pull tracks (${pullTrackError.errorCode}): ${pullTrackError.errorDescription}`);
            this.clear();
            config.onEvent && config.onEvent(PlayerStatus.Error);
            return;
        }

        this.pulledTracks = pullResponse.tracks;
        this.deliverySessionId = pullResponse.sessionId;

        const resolvingTracks = Promise.all(
            pullResponse.tracks.map(
                ({ mid }) =>
                // This will resolve when the track for the corresponding mid is added.
                    new Promise<MediaStreamTrack>((res, rej) => {
                        const trackTimeout = setTimeout(()=>{
                            streamingLoggerStore.log({
                                type: LogType.AppDelivery,
                                message: 'StreamingProvider: CloudflareCall: Delivery: tracks is not resolved',
                            });
                            rej();
                        }, 10_000);
                        const handleTrack = ({ transceiver, track }: RTCTrackEvent) => {
                            if (transceiver.mid !== mid) return;
                            clearTimeout(trackTimeout);
                            peer.removeEventListener(
                                'track',
                                handleTrack
                            );
                            res(track);
                        };
                        peer.addEventListener(
                            'track',
                            handleTrack
                        );
                        this.trackTimeout.push(trackTimeout);
                    })
            )
        );

        if (pullResponse.requiresImmediateRenegotiation) {
            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: 'StreamingProvider: CloudflareCall: Delivery: set remote sdp',
            });
            const bindedSetRemoteSdp =
                peer.setRemoteDescription
                    .bind(peer, pullResponse.sessionDescription) as (() => Promise<void>);

            const remoteDescriptionResp =
                await this.callWithTryCatch('rtc_set_remote_description_error', bindedSetRemoteSdp);

            if (!remoteDescriptionResp === null) {
                this.clear();
                config.onEvent && config.onEvent(PlayerStatus.Error);
                return;
            }

            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: 'StreamingProvider: CloudflareCall: Delivery: create answer',
            });
            // Create an answer
            const remoteAnswer = await this.callWithTryCatch<RTCSessionDescriptionInit>(
                'rtc_create_offer_error',
                peer.createAnswer.bind(peer)
            );
            if (!remoteAnswer) {
                this.clear();
                config.onEvent && config.onEvent(PlayerStatus.Error);
                return;
            }

            // And set it as local description
            const bindedSetLocalSdp = peer.setLocalDescription.bind(peer, remoteAnswer) as () => Promise<void>;
            const setLocalDescriptionResp =
                await this.callWithTryCatch('rtc_set_local_description_error', bindedSetLocalSdp);

            if (setLocalDescriptionResp === null) {
                this.clear();
                config.onEvent && config.onEvent(PlayerStatus.Error);
                return;
            }

            if (!remoteAnswer.sdp) {
                streamingLoggerStore.log({
                    type: LogType.AppDelivery,
                    message: 'StreamingProvider: CloudflareCall: Delivery: sdp is undefined',
                });
                console.error('Sdp is undefined');
                this.clear();
                config.onEvent && config.onEvent(PlayerStatus.Error);
                return;
            }

            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: 'StreamingProvider: CloudflareCall: Delivery: renegotiation',
            });
            const renegotiateStatus =
                await this.renegotiate(this.deliverySessionId, remoteAnswer.sdp);

            if (!renegotiateStatus) {
                streamingLoggerStore.log({
                    type: LogType.AppDelivery,
                    message: 'StreamingProvider: CloudflareCall: Delivery: unsuccessful renegotiation',
                });
                this.clear();
                config.onEvent && config.onEvent(PlayerStatus.Error);
                return;
            }
            try {
                streamingLoggerStore.log({
                    type: LogType.AppDelivery,
                    message: 'StreamingProvider: CloudflareCall: Delivery: creating media stream',
                });
                const remoteVideoStream = new MediaStream();
                videoElement.srcObject = remoteVideoStream;
                const pulledTracks = await resolvingTracks;
                pulledTracks.forEach((t) => remoteVideoStream.addTrack(t));
                streamingLoggerStore.log({
                    type: LogType.AppDelivery,
                    message: 'StreamingProvider: CloudflareCall: Delivery: media stream created',
                });
            } catch (e) {
                console.log(e);
                streamingLoggerStore.log(
                    {
                        type: LogType.AppDelivery,
                        message: 'StreamingProvider: CloudflareCall: Delivery: creating media stream error',
                    },
                    { errorText: (e as Error).message },
                    { includeExtendedData: true }
                );
                this.clear();
                config.onEvent && config.onEvent(PlayerStatus.Error);
                return;
            }
        }
    }

    private async renegotiate(sessionId: string, sdp: string) {
        try {
            await callDeliveryRenegotiate(sessionId, sdp);
            return true;
        } catch (error) {
            console.error(error);
            this.statsCallback(
                'rtc_renegotiate_error',
                { errorText: (error as Error).message });
            return false;
        }
    }

    private async pullTracks(tracksToPull: TracksToPullObject[], sessionId?: string) {
        try {
            const resp = await callDeliveryConfig(tracksToPull, sessionId);
            const pullResponse = parseResponse(resp);
            return pullResponse;
        } catch (error) {
            this.statsCallback(
                'rtc_pull_tracks_error',
                { errorText: (error as Error).message });
            console.error(error);
            return null;
        }
    }

    private startCollectingPlayerStats() {
        const peer = this.peerConnection;

        Emitter.emit(Events.STREAMING_STATS_SET_DELIVERY_START_TIMER);
        Emitter.emit(Events.STREAMING_STATS_WEBRTC_WATCH_PEER_CONNECTION, peer);
    }

    private attachPeerConnectionEvents(updateStatus?: (event: PlayerStatus) => void) {
        if (!(this.peerConnection instanceof RTCPeerConnection)) {
            return;
        }

        const callback = async () => {
            switch (this.peerConnection?.connectionState) {
                case 'new':
                    streamingLoggerStore.log({
                        type: LogType.WebRTCDelivery,
                        message: 'StreamingProvider: CloudflareCall: connectionState new',
                    });
                    break;
                case 'connecting':
                    streamingLoggerStore.log({
                        type: LogType.WebRTCDelivery,
                        message: 'StreamingProvider: CloudflareCall: connectionState connecting',
                    });
                    break;
                case 'connected':
                    streamingLoggerStore.log({
                        type: LogType.WebRTCDelivery,
                        message: 'StreamingProvider: CloudflareCall: connectionState connected',
                    });
                    updateStatus && updateStatus(PlayerStatus.PeerConnectionEstablished);
                    break;
                case 'failed':
                    streamingLoggerStore.log({
                        type: LogType.WebRTCDelivery,
                        message: 'StreamingProvider: CloudflareCall: connectionState failed',
                    });
                    break;
                default:
                    streamingLoggerStore.log(
                        {
                            type: LogType.WebRTCDelivery,
                            message: 'StreamingProvider: CloudflareCall: connectionState unknown',
                        },
                        { state: this.peerConnection?.connectionState },
                        { includeExtendedData: true }
                    );
                    break;
            }
        };

        this.peerConnection.addEventListener('connectionstatechange', callback);
        this.peerConnection.addEventListener(
            'icecandidateerror',
            (error) => {
                streamingLoggerStore.log({
                    type: LogType.WebRTCDelivery,
                    message: 'StreamingProvider: CloudflareCall: icecandidateerror',
                },
                { ...error },
                { includeExtendedData: true });
                this.statsCallback(
                    'rtc_gather_candidates_error',
                    { errorText: (error as RTCPeerConnectionIceErrorEvent).errorText }
                );
            }
        );

        this.peerConnection.oniceconnectionstatechange = () => {
            streamingLoggerStore.log(
                {
                    type: LogType.AppDelivery,
                    message: 'StreamingProvider: CloudflareCall: iceconnectionstate changed',
                },
                { iceCandidate: this.peerConnection?.iceConnectionState },
                { includeExtendedData: true }
            );
        };
        this.statsInterval = setInterval(this.onConnectionStats.bind(this), this.msStatsInterval);
    };

    async stopDelivery() {
        streamingLoggerStore.log({
            type: LogType.AppDelivery,
            message: 'StreamingProvider: CloudflareCall: Stop Delivery: stop',
        });
        this.trackTimeout.forEach((t) => clearTimeout(t));
        this.trackTimeout = [];
        clearTimeout(this.statsInterval);
        if (!this.peerConnection || this.peerConnection.connectionState === 'closed') {
            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: 'StreamingProvider: CloudflareCall: Stop Delivery: no peer',
            });
            this.clear();
            return;
        }
        this.peerConnection.ontrack = null;

        if (!this.deliverySessionId) {
            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: 'StreamingProvider: CloudflareCall: Stop Delivery: no deliverySessionId',
            });
            this.clear();
            return;
        }

        if (!this.pulledTracks || !this.pulledTracks.length) {
            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: 'StreamingProvider: CloudflareCall: Stop Delivery: no pulledTracks',
            });
            this.clear();
            return;
        }

        const transceivers = this.peerConnection.getTransceivers();
        const tracks = this.pulledTracks?.map((track) => {return { mid: track.mid };});
        transceivers.forEach((t) => {
            // eslint-disable-next-line no-param-reassign
            t.direction = 'inactive';
        });

        streamingLoggerStore.log({
            type: LogType.AppDelivery,
            message: 'StreamingProvider: CloudflareCall: Stop Delivery: createOffer',
        });
        const localSdp = this.peerConnection.localDescription?.sdp;
        const sdp = await this.callWithTryCatch<RTCSessionDescriptionInit>(
            'rtc_create_offer_error',
            this.peerConnection.createOffer.bind(this.peerConnection)
        );
        if (!sdp) {
            console.error('Error to get create sdp');
            this.clear();
            return;
        }
        streamingLoggerStore.log({
            type: LogType.AppDelivery,
            message: 'StreamingProvider: CloudflareCall: Stop Delivery: set local sdp',
        });
        const bindedSetLocalSdp = this.peerConnection
            .setLocalDescription.bind(this.peerConnection, sdp) as () => Promise<void>;
        await this.callWithTryCatch('rtc_set_local_description_error', bindedSetLocalSdp);

        streamingLoggerStore.log({
            type: LogType.AppDelivery,
            message: 'StreamingProvider: CloudflareCall: Stop Delivery: close tracks',
        });
        const result = await this.closeTracks({
            sessionId: this.deliverySessionId,
            sdp: localSdp,
            tracks,
        });
        if (!result) {
            console.log('Close tracks error');
            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: 'StreamingProvider: CloudflareCall: Stop Delivery: close tracks error',
            });
            this.clear();
            return;
        }
        this.pulledTracks = undefined;
        if (result?.sessionDescription) {
            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: 'StreamingProvider: CloudflareCall: Stop Delivery: set remote sdp',
            });
            const bindedSetRemoteSdp =
                    this.peerConnection.setRemoteDescription
                        .bind(this.peerConnection, result?.sessionDescription) as (() => Promise<void>);
            const setRemoteDescriptionResp =
                await this.callWithTryCatch('rtc_set_remote_description_error', bindedSetRemoteSdp);
            if (setRemoteDescriptionResp === null) {
                this.clear();
                return;
            }
        }

    }

    private clear() {
        this.trackTimeout.forEach((t) => clearTimeout(t));
        this.trackTimeout = [];
        clearTimeout(this.statsInterval);
        this.peerConnection?.close();
        this.peerConnection = undefined;
        this.pulledTracks = undefined;
        this.deliverySessionId = undefined;
    }

    private async closeTracks(body: CloseTracks) {
        try {
            const resp = await callCloseTracks(body, true);
            const data = parseResponse(resp);
            if (!data) {
                throw new Error('Cloudflare Call Session is not generated');
            }
            return data;
        } catch (error) {
            console.error(error);
            this.statsCallback('rtc_broadcast_close_call_error', { errorText: (error as Error).message });
            return null;
        }
    }

    private async onConnectionStats() {
        if (this.peerConnection) {
            let bytesReceivedBlock = 0;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const stats = await this.peerConnection.getStats(null).catch(_ => []);
            stats.forEach((report)=>{
                if (report.type.match('inbound-rtp')) bytesReceivedBlock += report.bytesReceived;
            });
            if (bytesReceivedBlock <= this.bytesReceived) {
                this.timeoutThresholdCounter += this.msStatsInterval;
                if (this.mediaTimeoutOccured === false && this.timeoutThresholdCounter >= this.mediaTimeoutThreshold) {
                    this.statsCallback('rtc_no_media', { errorText: 'NO_MEDIA' });
                    this.mediaTimeoutOccured = true;
                }
            } else {
                this.bytesReceived = bytesReceivedBlock;
                this.timeoutThresholdCounter = 0;
                if (this.mediaTimeoutOccured == true) {
                    this.statsCallback('rtc_media_recovered', { errorText: 'MEDIA_RECOVERED' });
                    this.mediaTimeoutOccured = false;
                }
            }
        }
    }

    private statsCallback(event: string, data: unknown) {
        const signalingState = this.peerConnection?.signalingState;
        const streamingType = 'delivery';

        Emitter.emit(Events.STREAMING_STATS_WEBRTC_SEND_EVENT, event, { ...data || {}, streamingType, signalingState });
    }

    private async callWithTryCatch<T>(event:string, callback: () => Promise<T>): Promise<T | null> {
        let result: T;
        try {
            result = await callback();
        } catch (e) {
            console.error(e);
            this.statsCallback(event, { errorText: (e as Error).message });
            return null;
        }
        return result;
    };

}
